import { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getTabulation, type Tabulation } from "../../../service/report-card"
import { all_routes } from "../../router/all_routes"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { getCampusLogoFallbackUrl } from "../../../utils/campusLogos"

// ── Full RMS-style class result sheet — opened in a new tab from the picker. ──
const MarksTabulationSheet = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [data, setData] = useState<Tabulation | null>(null)
  const [loading, setLoading] = useState(true)

  const breakdown = params.get("breakdown") || ""
  const classId = params.get("class") || ""
  const section = params.get("section") || ""
  const autoprint = params.get("autoprint") === "1"

  useEffect(() => {
    if (!breakdown || !classId || !section) return
    setLoading(true)
    getTabulation({ breakdown, class: classId, section })
      .then(r => setData(r.data))
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to build tabulation"))
      .finally(() => setLoading(false))
  }, [breakdown, classId, section])

  useEffect(() => {
    if (autoprint && data) {
      const t = setTimeout(() => window.print(), 300)
      return () => clearTimeout(t)
    }
  }, [autoprint, data])

  if (!breakdown || !classId || !section) {
    return <div className="p-4 text-muted">Missing breakdown, class or section in the URL.</div>
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`@media print {
          @page { size: A3 landscape; margin: 8mm; }
          /* .table-responsive's overflow-x:auto only shows the scrolled-into-view
             slice on screen -- without this, print captures whatever was scrolled
             into view and clips the rest of the table instead of the whole sheet. */
          .table-responsive { overflow: visible !important; }
        }`}</style>
        <div className="d-flex align-items-center justify-content-between mb-3 no-print">
          <button className="btn btn-light btn-sm" onClick={() => navigate(all_routes.marksTabulation)}>
            ← Back to picker
          </button>
          <button className="btn btn-dark btn-sm" onClick={() => window.print()} disabled={!data}>
            Print (A3)
          </button>
        </div>

        {loading ? (
          <div className="text-center text-muted py-5">Building result sheet…</div>
        ) : !data ? (
          <div className="text-center text-muted py-5">Could not load this result sheet.</div>
        ) : (
          <Sheet data={data} />
        )}
      </div>
    </div>
  )
}

// Bootstrap's base .badge class defaults text to white, assuming a .bg-*
// class always supplies a background -- with neither, a grade whose config
// has no colorCode set rendered as white text on the table's white
// background (invisible on screen, only visible in print, which normalizes
// text to black). A grey fallback keeps it readable either way.
const gradeBadgeStyle = (colorCode: string | null) =>
  colorCode ? { background: colorCode, color: "#fff" } : { background: "#e5e7eb", color: "#111827" }

// Ported from RMS's MarksTabulation.jsx shortSubjectName() verbatim -- RMS's
// A3 sheet collapses common multi-word subject names to standard short forms
// so 12 subjects' worth of columns fit on one page. Display-only: the full
// name stays in data.subjects[].subjectName and is still shown in a tooltip,
// same as RMS does with its title={s.subject_name}.
// Order matters: longer phrases first so they win over single-word
// substitutions that would otherwise mangle them ("INTEGRATED LEARNING" must
// become "INTG. LEARN." before any word-by-word expansion runs).
const shortSubjectName = (name: string) => {
  let s = String(name || "")
  s = s
    .replace(/\bPHYSICAL\s+EDUCATION\b/gi, "P.E.")
    .replace(/\bINTEGRATED\s+LEARNING\b/gi, "INTG. LEARN.")
    .replace(/\bGENERAL\s+KNOWLEDGE\b/gi, "GK")
    .replace(/\bSOCIAL\s+STUDIES\b/gi, "SOC. STUD.")
    .replace(/\bENVIRONMENTAL\s+STUDIES\b/gi, "EVS")
    .replace(/\bCOMPUTER\s+SCIENCE\b/gi, "COMP. SCI.")
    .replace(/\bINFORMATION\s+TECHNOLOGY\b/gi, "IT")
  s = s
    .replace(/\bLITERATURE\b/gi, "LIT.")
    .replace(/\bLANGUAGE\b/gi, "LANG.")
    .replace(/\bMATHEMATICS\b/gi, "MATHS")
  return s.replace(/\s+/g, " ").trim()
}

const Sheet = ({ data }: { data: Tabulation }) => {
  const currentBranch = useSelector(selectCurrentBranch)
  const subjects = data.subjects
  // Grade-only subjects (PE/Art/Nazra — not numerically counted) show just a Grade column;
  // everything else shows its raw subcategories (or a single "Marks" bucket) plus Total.
  const colsFor = (s: Tabulation["subjects"][number]) =>
    s.showGradeOnly
      ? ["Grade"]
      : (s.subCategories.length > 0 ? s.subCategories.map(sc => sc.description) : ["Marks"]).concat(["Total"])

  const cellFor = (st: Tabulation["students"][number], courseStructureId: string) =>
    st.subjects.find(s => s.courseStructureId === courseStructureId)

  const fmt = (n: number | null) => (n == null ? "—" : Number(n).toFixed(2))

  return (
    <div className="printable-content">
      <div className="text-center mb-2">
        {currentBranch?.logoUrl ? (
          <img
            src={currentBranch.logoUrl}
            alt=""
            style={{ width: "50px", height: "50px", objectFit: "contain" }}
            onError={(e) => {
              // The presigned S3 URL occasionally hasn't resolved/loaded in
              // time (new-tab bootstrap race) -- fall back to the static
              // campus logo once instead of leaving a broken-image icon on
              // a result sheet, same fallback used everywhere else.
              const fallback = getCampusLogoFallbackUrl(currentBranch.campusNo, "main")
              const img = e.currentTarget
              if (fallback && img.src !== window.location.origin + fallback) {
                img.onerror = null
                img.src = fallback
              } else {
                img.style.display = "none"
              }
            }}
          />
        ) : null}
        <h5 className="mb-0 fw-bold">{data.school.name}</h5>
        <div className="fw-semibold">
          {data.class.name} Section {data.section.name} Result Sheet
        </div>
        <div className="text-primary small">{data.breakdown.title}</div>
      </div>

      {data.gradeLegend.length > 0 && (
        <div className="d-flex justify-content-end gap-1 mb-2">
          {data.gradeLegend.map(g => (
            <span key={g.grade} className="badge" style={gradeBadgeStyle(g.colorCode)}>
              {g.grade}
            </span>
          ))}
        </div>
      )}

      <style>{`
        /* .printable-content forces a blue th background for simpler printed
           reports (see _print.scss) -- RMS's sheet has no such coloring, so
           this table opts back out to plain white/black like RMS. */
        @media print {
          .printable-content .tab-sheet-table th {
            background-color: #fff !important;
            color: #000 !important;
          }
          /* Browsers drop background colors on print by default (to save
             ink) unless told otherwise -- the grade/subject-total badges
             (set via inline style, not a .bg-* class) went from colored on
             screen to plain black-on-white in print/PDF. Scoped to the
             whole .printable-content, not just the table, because the
             grade-legend row (the A+/A/B/C/D/F chips) renders as a sibling
             above the table, not inside it. */
          .printable-content,
          .printable-content * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
        /* Global .table th/td rules (see _tables.scss) target <th>/<td>
           directly, which beats the inline 0.7rem set on <table> -- that
           inline style only governs the table element's own font-size, not
           its children's, so without this every cell except .v-header was
           stuck at the site-wide 14px instead of matching the sheet's own
           0.7rem, which is why header/body cells looked mismatched in size. */
        .tab-sheet-table th,
        .tab-sheet-table td {
          font-size: 0.7rem !important;
        }
        .tab-sheet-table th.v-header {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          white-space: nowrap;
          width: 22px;
          max-width: 22px;
          font-size: 0.7rem;
          line-height: 1;
          padding: 4px 2px;
          text-align: left;
          vertical-align: middle;
        }
      `}</style>
      <div className="table-responsive">
        <table className="table table-bordered table-sm mb-0 align-middle text-center tab-sheet-table" style={{ fontSize: "0.7rem" }}>
          <thead>
            <tr>
              <th rowSpan={3}>GR No</th>
              <th rowSpan={3}>Student Name</th>
              <th rowSpan={3}>Roll No</th>
              {subjects.map(s => (
                <th key={s.courseStructureId} colSpan={colsFor(s).length} title={s.subjectName}>{shortSubjectName(s.subjectName)}</th>
              ))}
              <th rowSpan={3}>Grand Total</th>
              <th rowSpan={3}>Out Of</th>
              <th rowSpan={3}>Result</th>
              <th rowSpan={3}>Grade</th>
              <th rowSpan={3}>% Percentage</th>
            </tr>
            <tr>
              {subjects.map(s =>
                colsFor(s).map(label => (
                  <th key={`${s.courseStructureId}-${label}`} className={label === "Total" || label === "Grade" ? "" : "v-header"}>
                    {label}
                  </th>
                ))
              )}
            </tr>
            <tr>
              {subjects.map(s => {
                const cols = colsFor(s)
                return cols.map((label, i) => {
                  if (label === "Total") return <td key={`${s.courseStructureId}-max-total`} className="text-muted">{s.totalMarks}</td>
                  if (label === "Grade") return <td key={`${s.courseStructureId}-max-grade`} className="text-muted">—</td>
                  const sc = s.subCategories[i]
                  return <td key={`${s.courseStructureId}-max-${i}`} className="text-muted">{sc?.marks ?? s.totalMarks}</td>
                })
              })}
            </tr>
          </thead>
          <tbody>
            {data.students.map(st => (
              <tr key={st.enrollmentId}>
                <td>{st.grNo}</td>
                <td className="text-nowrap text-start">{st.studentName}</td>
                <td>{st.rollNo || "—"}</td>
                {subjects.map(s => {
                  const subj = cellFor(st, s.courseStructureId)
                  const cols = colsFor(s)
                  return cols.map((label, i) => {
                    if (label === "Total") {
                      return (
                        <td key={`${s.courseStructureId}-total`}>
                          {subj?.allNa ? "NA" : subj?.isAbsent ? "AB" : (
                            <span
                              className={`badge ${subj?.colorCode ? "" : "bg-success-transparent text-success"}`}
                              style={gradeBadgeStyle(subj?.colorCode ?? null)}
                            >
                              {fmt(subj?.subjectTotal ?? null)}
                            </span>
                          )}
                        </td>
                      )
                    }
                    if (label === "Grade") {
                      return <td key={`${s.courseStructureId}-grade`}>{subj?.gradeLetter ?? "—"}</td>
                    }
                    const cell = subj?.cells[i]
                    return (
                      <td key={`${s.courseStructureId}-${i}`}>
                        {!cell ? "—" : cell.is_na ? "NA" : cell.is_absent ? "AB" : fmt(cell.value)}
                      </td>
                    )
                  })
                })}
                <td className="fw-medium">{fmt(st.grandTotal)}</td>
                <td className="text-muted">{fmt(st.grandMax)}</td>
                <td>
                  {st.result === "Pass"
                    ? <span className="badge bg-success-transparent text-success">Pass</span>
                    : <span className="badge bg-danger-transparent text-danger">Fail</span>}
                </td>
                <td>
                  <span className="badge" style={gradeBadgeStyle(st.colorCode)}>{st.gradeLetter ?? "—"}</span>
                </td>
                <td>{st.grandMax ? fmt(st.percent) + "%" : "0.00%"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row mt-4">
        <div className="col-4">
          <p className="mb-3">No. of students appeared <strong>{data.summary.appeared}</strong></p>
          <p className="border-top pt-1" style={{ marginTop: "50px" }}>Sign of checker</p>
        </div>
        <div className="col-4">
          <p className="mb-1">No. of students passed <strong>{data.summary.passed}</strong></p>
          <p className="mb-3">
            Passed Percentage{" "}
            <strong>{data.summary.appeared ? ((data.summary.passed / data.summary.appeared) * 100).toFixed(0) : 0}%</strong>
          </p>
          <p className="border-top pt-1" style={{ marginTop: "50px" }}>Sign of class teacher</p>
          <p className="border-top pt-1" style={{ marginTop: "50px" }}>Sign of Dy.Head</p>
        </div>
        <div className="col-4">
          <p className="mb-3">No. of students Failed <strong>{data.summary.failed}</strong></p>
          <p className="border-top pt-1" style={{ marginTop: "50px" }}>Sign of Principal</p>
        </div>
      </div>
    </div>
  )
}

export default MarksTabulationSheet
